import React from "react";
import PropTypes from "prop-types";

/**
 * Általános Modal komponens különböző típusú modálablakokhoz
 *
 * @param {boolean} isOpen - A modal megjelenítése/elrejtése
 * @param {function} onClose - Modal bezárásakor hívandó függvény
 * @param {function} onConfirm - Megerősítés gomb kattintásakor hívandó függvény
 * @param {function} onSecondary - Másodlagos gomb kattintásakor hívandó függvény
 * @param {string} title - A modal címe
 * @param {React.ReactNode} children - A modal tartalma
 * @param {string} confirmText - A megerősítő gomb szövege
 * @param {string} cancelText - A mégsem gomb szövege
 * @param {string} secondaryText - A másodlagos gomb szövege
 * @param {string} confirmClass - A megerősítő gomb CSS osztálya
 * @param {string} cancelClass - A mégsem gomb CSS osztálya
 * @param {string} secondaryClass - A másodlagos gomb CSS osztálya
 * @param {string} type - A modal típusa (danger, success, warning, info, primary)
 * @param {boolean} showFooter - Lábrész megjelenítése
 * @param {boolean} showCancel - Mégsem gomb megjelenítése
 * @param {boolean} showSecondary - Másodlagos gomb megjelenítése
 * @param {string} width - A modal szélessége (sm, md, lg, xl)
 * @param {boolean} centered - A modal középre igazítása
 */
const Modal = ({
	isOpen,
	onClose,
	onConfirm,
	onSecondary,
	title,
	children,
	confirmText = "Rendben",
	cancelText = "Mégsem",
	secondaryText,
	confirmClass,
	cancelClass,
	secondaryClass,
	type = "primary", // danger, success, warning, info, primary
	showFooter = true,
	showCancel = true,
	showSecondary = false,
	width = "md", // sm, md, lg, xl
	centered = true,
	animation = true,
	icon = null,
}) => {
	if (!isOpen) return null;

	const modalSizeClass =
		{
			sm: "modal-sm",
			md: "",
			lg: "modal-lg",
			xl: "modal-xl",
		}[width] || "";

	const statusClass =
		{
			danger: "bg-danger",
			success: "bg-success",
			warning: "bg-warning",
			info: "bg-info",
			primary: "bg-primary",
			secondary: "bg-secondary",
			dark: "bg-dark",
			light: "bg-light",
			"elit-lila": "bg-elit-lila",
			"elit-narancs": "bg-elit-narancs",
		}[type] || "";

	const defaultBtnClass =
		{
			danger: "btn-danger",
			success: "btn-success",
			warning: "btn-warning",
			info: "btn-info",
			primary: "btn-primary",
			secondary: "btn-secondary",
			dark: "btn-dark",
			light: "btn-light",
			"elit-lila": "btn-elit-lila",
			"elit-narancs": "btn-elit-narancs",
		}[type] || "btn-primary";

	const confirmButtonClass = confirmClass || defaultBtnClass;
	const cancelButtonClass = cancelClass || "btn-outline-secondary";
	const secondaryButtonClass = secondaryClass || defaultBtnClass;

	return (
		<div className={`modal modal-blur ${animation ? "fade" : ""} ${isOpen ? "show" : ""}`} style={{ display: isOpen ? "block" : "none", background: "rgba(0,0,0,0.5)" }} tabIndex="-1" role="dialog" aria-modal="true">
			<div className={`modal-dialog ${centered ? "modal-dialog-centered" : ""} ${modalSizeClass}`} role="document">
				<div className="modal-content">
					{statusClass && <div className={`modal-status ${statusClass}`}></div>}

					{title && (
						<div className="modal-header">
							<h5 className="modal-title">{title}</h5>
							{onClose && <button type="button" className="btn-close" aria-label="Bezárás" onClick={onClose}></button>}
						</div>
					)}

					<div className="modal-body text-center py-3">
						{icon && <div className="mb-3">{icon}</div>}
						{children}
					</div>

					{showFooter && (
						<div className="modal-footer">
							<div className="w-100">
								<div className="row">
									{(() => {
										const buttons = [];
										const visibleButtonCount = (showCancel ? 1 : 0) + (showSecondary && onSecondary ? 1 : 0) + (onConfirm ? 1 : 0);

										if (visibleButtonCount === 0) return null;

										const colClass = visibleButtonCount === 1 ? "col-12" : visibleButtonCount === 2 ? "col-6" : "col-4";

										if (showCancel) {
											buttons.push(
												<div key="cancel" className={colClass}>
													<button type="button" className={`btn ${cancelButtonClass} w-100`} onClick={onClose}>
														{cancelText}
													</button>
												</div>
											);
										}

										if (showSecondary && onSecondary) {
											buttons.push(
												<div key="secondary" className={colClass}>
													<button type="button" className={`btn ${secondaryButtonClass} w-100`} onClick={onSecondary}>
														{secondaryText}
													</button>
												</div>
											);
										}

										if (onConfirm) {
											buttons.push(
												<div key="confirm" className={colClass}>
													<button type="button" className={`btn ${confirmButtonClass} w-100`} onClick={onConfirm}>
														{confirmText}
													</button>
												</div>
											);
										}

										return buttons;
									})()}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

Modal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func,
	onConfirm: PropTypes.func,
	onSecondary: PropTypes.func,
	title: PropTypes.string,
	children: PropTypes.node,
	confirmText: PropTypes.string,
	cancelText: PropTypes.string,
	secondaryText: PropTypes.string,
	confirmClass: PropTypes.string,
	cancelClass: PropTypes.string,
	secondaryClass: PropTypes.string,
	type: PropTypes.string,
	showFooter: PropTypes.bool,
	showCancel: PropTypes.bool,
	showSecondary: PropTypes.bool,
	width: PropTypes.string,
	centered: PropTypes.bool,
	animation: PropTypes.bool,
	icon: PropTypes.node,
};

export default Modal;
